import { useState } from 'react';
import { Hint, Panel, Segmented } from './ui/Controls';
import { useStore } from '@/state/store';
import { getImage } from '@/engine/pipeline';
import { downloadBlob, exportDesign, type ExportFormat } from '@/engine/render/exporter';
import { getFormat } from '@/lib/formats';
import { formatBytes } from '@/lib/utils';

const SCALES = [
  { value: 1, label: '1×' },
  { value: 2, label: '2×' },
  { value: 4, label: '4×' },
];

export function ExportPanel() {
  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);
  const photoUrl = useStore((s) => s.photoDataUrl);
  const logoUrl = useStore((s) => s.logoDataUrl);

  const [format, setFormat] = useState<ExportFormat>('png');
  const [scale, setScale] = useState(2);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const def = getFormat(spec?.format ?? 'ig-portrait');
  const outW = Math.round(def.width * scale);
  const outH = Math.round(def.height * scale);

  const run = async () => {
    if (!spec) return;
    setBusy(true);
    setError(null);
    try {
      const photo = photoUrl ? await getImage(photoUrl) : null;
      const logo = logoUrl ? await getImage(logoUrl) : null;
      const result = await exportDesign(spec, photo, logo, {
        format,
        scale,
        quality: format === 'jpg' ? 0.92 : 1,
      });
      downloadBlob(result.blob, result.filename);
      setLast(`${result.filename} · ${formatBytes(result.blob.size)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Export">
      <div className="space-y-2.5">
        <div>
          <span className="field-label">File type</span>
          <Segmented<ExportFormat>
            options={[
              { value: 'png', label: 'PNG' },
              { value: 'jpg', label: 'JPG' },
              { value: 'png-transparent', label: 'PNG α', title: 'Transparent background — text, overlays and logo only' },
            ]}
            value={format}
            onChange={setFormat}
          />
        </div>

        <div>
          <span className="field-label">Resolution</span>
          <Segmented
            options={SCALES.map((s) => ({
              value: String(s.value),
              label: s.label,
              title: `${Math.round(def.width * s.value)}×${Math.round(def.height * s.value)}`,
            }))}
            value={String(scale)}
            onChange={(v) => setScale(Number(v))}
          />
        </div>

        <div className="flex items-baseline justify-between rounded-md bg-white/[0.03] px-2.5 py-1.5">
          <span className="text-[10px] text-ink-400">Output</span>
          <span className="font-mono text-[10px] tabular-nums text-ink-200">
            {outW} × {outH}
          </span>
        </div>

        <button type="button" className="btn-primary w-full" disabled={!spec || busy} onClick={() => void run()}>
          {busy ? 'Rendering…' : `Download ${format === 'jpg' ? 'JPG' : 'PNG'}`}
        </button>

        {last && <p className="truncate text-[10px] text-emerald-400">Saved {last}</p>}
        {error && <p className="text-[10px] text-red-400">{error}</p>}

        {format === 'png-transparent' && (
          <Hint>
            Exports the type, overlays and logo with no photo behind them —
            useful as a layer over video or a different background. Full-frame
            washes will still appear, since they are part of the design.
          </Hint>
        )}
        {scale > 1 && (
          <Hint>
            Re-rendered at {outW}px rather than upscaled, so type stays sharp at
            print sizes.
          </Hint>
        )}
      </div>
    </Panel>
  );
}
